// Minimal path-based client-side routing built directly on the History API.
// Intentionally tiny: this app must not add react-router or any other
// routing dependency, so App.jsx and the module route wrappers use this
// instead.
import { useEffect, useState, useCallback } from 'react';

// Exported so a second, independent hook (useSearchParams below) can listen
// for the same navigation signal useRoute() does, without useRoute() itself
// needing to change its return shape (every existing call site destructures
// it as a plain pathname string).
export const NAVIGATE_EVENT = 'tgmsaas:navigate';

/** Push a new path onto history and notify all useRoute() subscribers. */
export function navigate(path, { replace = false } = {}) {
  if (typeof window === 'undefined') return;
  if (window.location.pathname === path) return;
  if (replace) {
    window.history.replaceState({}, '', path);
  } else {
    window.history.pushState({}, '', path);
  }
  window.dispatchEvent(new Event(NAVIGATE_EVENT));
}

/** React hook returning the current pathname, updating on navigation. */
export function useRoute() {
  const [pathname, setPathname] = useState(() =>
    typeof window !== 'undefined' ? window.location.pathname : '/'
  );

  const sync = useCallback(() => {
    setPathname(window.location.pathname);
  }, []);

  useEffect(() => {
    window.addEventListener('popstate', sync);
    window.addEventListener(NAVIGATE_EVENT, sync);
    return () => {
      window.removeEventListener('popstate', sync);
      window.removeEventListener(NAVIGATE_EVENT, sync);
    };
  }, [sync]);

  return pathname;
}

/** React hook returning the current URL's query string as a URLSearchParams,
 * updating on navigation -- the same pattern as useRoute(), just for the
 * query string instead of the pathname. First needed by Phase 17's search
 * results page to read `?q=`. */
export function useSearchParams() {
  const [params, setParams] = useState(() =>
    typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : new URLSearchParams()
  );

  const sync = useCallback(() => {
    setParams(new URLSearchParams(window.location.search));
  }, []);

  useEffect(() => {
    window.addEventListener('popstate', sync);
    window.addEventListener(NAVIGATE_EVENT, sync);
    return () => {
      window.removeEventListener('popstate', sync);
      window.removeEventListener(NAVIGATE_EVENT, sync);
    };
  }, [sync]);

  return params;
}

/** Link-like click handler helper: navigates without a full page reload. */
export function navigationClickHandler(path) {
  return (event) => {
    if (event) event.preventDefault();
    navigate(path);
  };
}
